import React from "react";
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PercentIcon from "@mui/icons-material/Percent";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "react-i18next";
import { InvoiceItem } from "../../types/invoice";
import { only2dec, formatEUR } from "../../utils/oc/ocCalc";

interface InvoiceItemsTableProps {
  items: InvoiceItem[];
  onUpdate: (idx: number, patch: Partial<InvoiceItem>) => void;
  onAdd: () => void;
  onDelete: (idx: number) => void;
  // Botón opcional que añade la línea de Fee Agencia con el fee de la empresa. Recibe el
  // evento porque, si la empresa tiene fee variable y fijo, ancla un menú para elegir.
  onAddFee?: (event: React.MouseEvent<HTMLElement>) => void;
  addFeeDisabled?: boolean;
}

// Comma → dot for decimal inputs (same behavior as the OCs items table).
const toDecimal = (v: string) => v.replace(",", ".");

const InvoiceItemsTable: React.FC<InvoiceItemsTableProps> = ({
  items,
  onUpdate,
  onAdd,
  onDelete,
  onAddFee,
  addFeeDisabled,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <Box>
      <Box sx={{ overflowX: "auto" }}>
        <Table
          size="small"
          sx={{
            minWidth: 640,
            "& thead th": {
              fontWeight: 700,
              bgcolor: theme.palette.neutral["100"],
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>{t("invoices.table.concepto")}</TableCell>
              <TableCell>{t("invoices.table.subtitulo")}</TableCell>
              <TableCell align="center">
                {t("invoices.table.subtotal")}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((it, idx) => (
              <TableRow key={`inv-item-${idx}`}>
                <TableCell sx={{ verticalAlign: "top", width: "42%" }}>
                  <TextField
                    fullWidth
                    multiline
                    size="small"
                    value={it.concepto}
                    onChange={(e) =>
                      onUpdate(idx, { concepto: e.target.value })
                    }
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: "top", width: "38%" }}>
                  <TextField
                    fullWidth
                    multiline
                    size="small"
                    value={it.subtitulo}
                    onChange={(e) =>
                      onUpdate(idx, { subtitulo: e.target.value })
                    }
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: "top", width: "16%" }}>
                  <TextField
                    fullWidth
                    size="small"
                    inputProps={{
                      inputMode: "decimal",
                      style: { textAlign: "right" },
                    }}
                    value={it.subtotal}
                    onChange={(e) =>
                      onUpdate(idx, { subtotal: toDecimal(e.target.value) })
                    }
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: "top", width: 48 }}>
                  <IconButton
                    size="small"
                    color="error"
                    aria-label={t("common.delete")}
                    disabled={items.length <= 1}
                    onClick={() => onDelete(idx)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
        }}
      >
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={onAdd}
          sx={{ width: "fit-content", textTransform: "none" }}
        >
          {t("invoices.table.addLine")}
        </Button>

        {onAddFee && (
          <Button
            variant="outlined"
            startIcon={<PercentIcon />}
            onClick={onAddFee}
            disabled={addFeeDisabled}
            sx={{ width: "fit-content", textTransform: "none" }}
          >
            {t("invoices.table.addFeeLine")}
          </Button>
        )}
      </Box>
    </Box>
  );
};

// Live total (display only — backend recalculates from subtotals).
export const sumSubtotals = (items: InvoiceItem[]): number =>
  only2dec(items.reduce((acc, it) => acc + only2dec(it.subtotal), 0));

export { formatEUR };

export default InvoiceItemsTable;
